import React, { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import useNavContext from "../../hooks/useNavContext";
import { flattenNav, getPrimaryTabs } from "./sidebarNavConfig";
import BottomTabBar from "../MobileNav/BottomTabBar";
import MenuSheet from "../MobileNav/MenuSheet";
import { useMobileNav } from "../MobileNav/MobileNavContext";
import { getNavModules } from "../NavV2/navModules";
import useNavPrefs from "../NavV2/navPrefs";

/**
 * Phone navigation: 4 pinned tabs + Menu (bottom sheet).
 *
 * Everything shown here comes from getNavGroups() via useNavContext - the same
 * source as the desktop Sidebar - so a person sees exactly the destinations
 * their role, industry and plan allow, with the same labels. (This used to be a
 * hand-copied list: crew and support saw Admin Setup and Analytics, which sent
 * them to Access Denied, and never saw My Tasks.)
 */
const MobileBottomNav = ({ vendorData, newMenu = false }) => {
  const { navGroups, persona } = useNavContext(vendorData);
  const { menuOpen, closeMenu, toggleMenu, account, actionsRef } = useMobileNav();
  const triggerRef = useRef(null);
  const { pathname } = useLocation();
  const { pins, splitDealership } = useNavPrefs();

  // New menu (preview): the sheet shows search, pinned pages and module tiles.
  const v2 = useMemo(
    () => (newMenu ? { ...getNavModules(navGroups, { splitDealership }), pins } : null),
    [newMenu, navGroups, splitDealership, pins]
  );

  const all = useMemo(() => {
    const flat = flattenNav(navGroups);
    // "Items" (/inventory) is a path prefix of its siblings - match exactly.
    return flat.map((item) => ({
      ...item,
      end: flat.some((other) => other !== item && other.to.startsWith(`${item.to}/`)),
    }));
  }, [navGroups]);

  const tabs = useMemo(() => {
    const picked = getPrimaryTabs(navGroups, persona);
    // The bar has ~70px per tab: drop a trailing "View" ("Calendar View" read
    // "Calendar Vi..."). The Menu sheet keeps the full label.
    return picked.map((tab) => {
      const full = all.find((item) => item.key === tab.key) || tab;
      return { ...full, label: full.label.replace(/\s+View$/, "") };
    });
  }, [navGroups, persona, all]);

  // The current page lives behind Menu when it is not one of the pinned tabs.
  const onPinned = tabs.some((tab) => pathname === tab.to || pathname.startsWith(`${tab.to}/`));
  const onMenuItem = all.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  const menuActive = !onPinned && onMenuItem;

  return (
    <>
      <BottomTabBar
        tabs={tabs}
        menuActive={menuActive}
        menuOpen={menuOpen}
        onMenu={toggleMenu}
        menuTriggerRef={triggerRef}
      />
      <MenuSheet
        open={menuOpen}
        onClose={closeMenu}
        navGroups={navGroups}
        v2={v2}
        account={account}
        actionsRef={actionsRef}
      />
    </>
  );
};

export default MobileBottomNav;
